import {useState} from 'react'
import ScaleLoader from 'react-spinners/ScaleLoader'
import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import {clsx} from 'clsx'
import type {Block} from '@/services/kontent-ai/types'
import {colorWithFallback} from '@/utilities/hooks/color-with-fallback'
import {requireHexCode} from '@/products/flexible-web-toolkit/colors'

export type JotFormContentItem = IContentItem<{
	tabTitle: Elements.TextElement
	jotformId: Elements.TextElement
	height: Elements.TextElement
	loaderColor: Elements.CustomElement
}>

export const JotFormBlock: Block<JotFormContentItem> = ({block, page}) => {
	const [isLoaded, setIsLoaded] = useState(false)

	// Function to sanitize and validate height
	const sanitizeHeight = (height: string | undefined): string => {
		if (!height) return '800px'

		// Check if height ends with "px"
		if (!height.endsWith('px')) return '800px'

		const parsedHeight = parseInt(height, 10)
		return isNaN(parsedHeight) || parsedHeight <= 0
			? '800px'
			: `${parsedHeight}px` // Validate and return with "px"
	}
	const formHeight = sanitizeHeight(block.elements.height)

	// Color priority system: block level > page level > fallback (using shared hook)
	const elancoNavyHex = requireHexCode('elanco-dark-navy')
	const {hexCode} = colorWithFallback({
		blockColor: block.elements.loaderColor,
		pageConfig: page.elements.config[0],
		fallbackHexColor: elancoNavyHex,
	})
	const finalLoaderColor: string = hexCode ?? elancoNavyHex

	return (
		<div data-clarity-mask="true">
			{!isLoaded && (
				<div className="flex w-full justify-center py-6">
					<ScaleLoader
						color={finalLoaderColor}
						height={40}
						loading
						margin={5}
						width={4}
					/>
				</div>
			)}
			<iframe
				allow="geolocation; microphone; camera"
				allowFullScreen
				className={clsx(!isLoaded && 'hidden')}
				data-testid="jotform-iframe"
				frameBorder="0"
				id={`JotFormIFrame-${block.elements.jotformId}`}
				onLoad={() => {
					setIsLoaded(true)
				}}
				src={`https://elancoah.jotform.com/${block.elements.jotformId}`}
				style={{
					minWidth: '100%',
					maxHeight: '100%',
					height: formHeight,
					border: 'none',
					overflow: 'scroll',
				}}
				title={`JotForm IFrame ${block.elements.jotformId}`}
			/>
		</div>
	)
}
